/**
 * Send-for-Approval drawer — mirrors KC2's "Send For Approval" action.
 * Fields: Approval Level, Approval State, Approver Type (Internal/External),
 * Approver, and Remark. Submits to POST /conversations/:id/send-for-approval.
 */
import { useEffect, useState, type JSX } from 'react'
import { ShieldCheck, Users, UserCheck } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useApprovalCountsStore } from '../../../store/approvalCountsStore.js'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, SelectField, textareaClass } from './primitives.js'

interface ApprovalLevel {
  id: string
  name: string
  description: string | null
}

interface ApprovalState {
  id: string
  name: string
  description: string | null
  colorCode: string | null
}

interface Approver {
  id: string
  name: string
  fullName: string | null
}

export interface SendForApprovalDrawerProps {
  open: boolean
  onClose: () => void
  conversationId: string
  onSent: () => void
}

export default function SendForApprovalDrawer({
  open,
  onClose,
  conversationId,
  onSent,
}: SendForApprovalDrawerProps): JSX.Element {
  const toast = useToast()
  const [levels, setLevels] = useState<ApprovalLevel[]>([])
  const [states, setStates] = useState<ApprovalState[]>([])
  const [approvers, setApprovers] = useState<Approver[]>([])
  const [loading, setLoading] = useState(false)
  const [levelId, setLevelId] = useState('')
  const [stateId, setStateId] = useState('')
  const [approverType, setApproverType] = useState<'internal' | 'external'>('internal')
  const [approverId, setApproverId] = useState('')
  const [remark, setRemark] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      api.get<{ levels: ApprovalLevel[] }>('/conversations/approval-levels'),
      api.get<{ states: ApprovalState[] }>('/conversations/approval-states'),
      api.get<{ approvers: Approver[] }>('/conversations/approvers?type=internal'),
    ])
      .then(([levelsRes, statesRes, approversRes]) => {
        if (cancelled) return
        setLevels(levelsRes.levels ?? [])
        setStates(statesRes.states ?? [])
        setApprovers(approversRes.approvers ?? [])
      })
      .catch(() => {
        if (!cancelled) {
          setLevels([])
          setStates([])
          setApprovers([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  // Refetch approvers when approverType changes
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setApproverId('')
    api
      .get<{ approvers: Approver[] }>(`/conversations/approvers?type=${approverType}`)
      .then((r) => {
        if (!cancelled) setApprovers(r.approvers ?? [])
      })
      .catch(() => {
        if (!cancelled) setApprovers([])
      })
    return () => {
      cancelled = true
    }
  }, [open, approverType])

  const submit = async (): Promise<void> => {
    if (!levelId || !stateId || !approverId) return
    setBusy(true)
    try {
      await api.post(`/conversations/${conversationId}/send-for-approval`, {
        approvalLevelId: levelId,
        approvalStateId: stateId,
        approverType,
        approverId,
        ...(remark.trim() ? { senderRemark: remark.trim() } : {}),
      })
      toast.success('Sent for approval')
      // Refresh the header approval badge so the new pending request is counted
      // immediately (the sender's "requested" tally, and the approver's queue).
      void useApprovalCountsStore.getState().refresh()
      onSent()
      onClose()
      // Reset form
      setLevelId('')
      setStateId('')
      setApproverId('')
      setRemark('')
    } catch (e) {
      toast.error('Could not send for approval', e instanceof Error ? e.message : 'Unexpected error')
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = levelId && stateId && approverId && !busy

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Send for Approval"
      subtitle="Submit this conversation for approval review"
      icon={<ShieldCheck className="h-4 w-4" />}
      info={{
        lead: 'Asks a named approver to sign this conversation off before it goes further.',
        points: [
          { term: 'Approval Level', detail: 'Which rung of your approval chain this request belongs to.' },
          { term: 'Approver Type', detail: 'Internal routes to a platform user; external routes to someone outside it.' },
          { term: 'Remark', detail: 'Context for the approver — what you want signed off and why.' },
          { term: 'While pending', detail: 'The conversation cannot be merged into another one until the request is decided.' },
        ],
      }}
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? 'Sending…' : 'Send for Approval'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {!loading && levels.length === 0 && states.length === 0 ? (
          <div className="rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-700">
            No approval levels or states are configured yet. Ask an administrator to create them under
            Conversation Management.
          </div>
        ) : (
          <>
            <Field label="Approval Level" required>
              <SelectField
                value={levelId}
                onChange={setLevelId}
                loading={loading}
                placeholder="Select Level"
                options={levels.map((l) => ({ value: l.id, label: l.name }))}
              />
            </Field>

            <Field label="Approval State" required>
              <SelectField
                value={stateId}
                onChange={setStateId}
                loading={loading}
                placeholder="Select State"
                options={states.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Field>

            <Field label="Approver Type">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setApproverType('internal')}
                  className={`flex items-center gap-1.5 rounded-[3px] border px-3 py-1.5 text-xs font-medium transition-colors ${
                    approverType === 'internal'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <UserCheck className="h-3.5 w-3.5" />
                  Internal
                </button>
                <button
                  type="button"
                  onClick={() => setApproverType('external')}
                  className={`flex items-center gap-1.5 rounded-[3px] border px-3 py-1.5 text-xs font-medium transition-colors ${
                    approverType === 'external'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Users className="h-3.5 w-3.5" />
                  External
                </button>
              </div>
            </Field>

            <Field label="Approver" required>
              <SelectField
                value={approverId}
                onChange={setApproverId}
                loading={loading}
                placeholder="Select Approver"
                options={approvers.map((a) => ({ value: a.id, label: a.name }))}
              />
            </Field>

            <Field label="Remark">
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                placeholder="Add a note for the approver (optional)…"
                className={textareaClass}
              />
            </Field>
          </>
        )}
      </div>
    </ActionDrawer>
  )
}
